import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../admin/firebase/config';
import './ProofOfDelivery.css';

const ProofOfDelivery = ({ order, onComplete, onCancel }) => {
  const [proofType, setProofType] = useState('otp'); // otp, photo, signature
  const [otp, setOtp] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  // Generate a simple OTP (in real app, this should be sent to customer)
  const generatedOtp = order.deliveryOtp || Math.floor(1000 + Math.random() * 9000).toString();

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const uploadPhoto = async () => {
    if (!photo) return null;

    try {
      const timestamp = Date.now();
      const storageRef = ref(storage, `delivery-proofs/${order.id}_${timestamp}.jpg`);
      await uploadBytes(storageRef, photo);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (err) {
      console.error('Error uploading photo:', err);
      throw new Error('Failed to upload photo');
    }
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setUploading(true);

      // Validate based on proof type
      if (proofType === 'otp') {
        if (!otp) {
          setError('Please enter OTP');
          setUploading(false);
          return;
        }
        if (otp !== generatedOtp) {
          setError('Invalid OTP. Please check with customer.');
          setUploading(false);
          return;
        }
      }

      let proofData = {
        type: proofType,
        timestamp: new Date().toISOString()
      };

      // Upload photo if selected
      if (proofType === 'photo' && photo) {
        const photoUrl = await uploadPhoto();
        proofData.photoUrl = photoUrl;
      } else if (proofType === 'otp') {
        proofData.otp = otp;
      }

      // Update order in Firestore
      const orderRef = doc(db, 'orders', order.id);
      await updateDoc(orderRef, {
        status: 'delivered',
        'deliveryStatus.deliveredAt': serverTimestamp(),
        'deliveryStatus.proofOfDelivery': proofData,
        updatedAt: serverTimestamp()
      });

      setUploading(false);
      onComplete();
    } catch (err) {
      console.error('Error submitting proof:', err);
      setError(err.message || 'Failed to submit proof of delivery');
      setUploading(false);
    }
  };

  return (
    <div className="proof-of-delivery-modal">
      <div className="proof-modal-overlay" onClick={onCancel}></div>
      <div className="proof-modal-content">
        <div className="proof-modal-header">
          <h2>📸 Proof of Delivery</h2>
          <button className="close-btn" onClick={onCancel}>✕</button>
        </div>

        <div className="proof-modal-body">
          <p className="proof-instruction">
            Please collect proof of delivery before marking as delivered
          </p>

          {/* Proof Type Selection */}
          <div className="proof-type-selector">
            <button
              className={`type-btn ${proofType === 'otp' ? 'active' : ''}`}
              onClick={() => setProofType('otp')}
            >
              🔢 OTP
            </button>
            <button
              className={`type-btn ${proofType === 'photo' ? 'active' : ''}`}
              onClick={() => setProofType('photo')}
            >
              📷 Photo
            </button>
          </div>

          {error && (
            <div className="proof-error">
              <span>⚠️ {error}</span>
            </div>
          )}

          {/* OTP Input */}
          {proofType === 'otp' && (
            <div className="proof-input-section">
              <label htmlFor="otp">Customer OTP</label>
              <p className="otp-note">
                Ask customer for the 4-digit OTP: <strong>{generatedOtp}</strong>
              </p>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="Enter 4-digit OTP"
                maxLength={4}
                className="otp-input"
                disabled={uploading}
              />
            </div>
          )}

          {/* Photo Upload */}
          {proofType === 'photo' && (
            <div className="proof-input-section">
              <label htmlFor="photo">Upload Photo</label>
              <p className="photo-note">
                Take a photo of the delivered order (max 5MB)
              </p>

              {photoPreview ? (
                <div className="photo-preview">
                  <img src={photoPreview} alt="Delivery proof" />
                  <button
                    className="remove-photo-btn"
                    onClick={() => {
                      setPhoto(null);
                      setPhotoPreview(null);
                    }}
                  >
                    Remove Photo
                  </button>
                </div>
              ) : (
                <div className="photo-upload-area">
                  <input
                    type="file"
                    id="photo"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoChange}
                    className="photo-input"
                    disabled={uploading}
                  />
                  <label htmlFor="photo" className="photo-upload-label">
                    <div className="upload-icon">📷</div>
                    <p>Tap to take photo</p>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Order Details */}
          <div className="order-summary">
            <h4>Order Summary</h4>
            <div className="summary-row">
              <span>Order #:</span>
              <strong>{order.orderNumber}</strong>
            </div>
            <div className="summary-row">
              <span>Customer:</span>
              <strong>{order.customerName}</strong>
            </div>
            <div className="summary-row">
              <span>Amount:</span>
              <strong>₹{order.totalAmount}</strong>
            </div>
            {order.paymentMethod === 'cod' && (
              <div className="cod-warning">
                ⚠️ Collect ₹{order.totalAmount} cash
              </div>
            )}
          </div>
        </div>

        <div className="proof-modal-footer">
          <button className="cancel-btn" onClick={onCancel} disabled={uploading}>
            Cancel
          </button>
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={uploading || (proofType === 'otp' && !otp) || (proofType === 'photo' && !photo)}
          >
            {uploading ? 'Processing...' : 'Mark as Delivered'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProofOfDelivery;
